import * as THREE from 'three'; // Assuming THREE is imported if needed
import { DragonflyController } from './DragonflyController';
import { Twist, Vec3 } from '../../../../models/SyntheticMap';
import { constrain, normalizeAngleToPi } from '../../../../utils/spatial';


export class DragonflyOrbitController extends DragonflyController {
  status: string = 'Orbiting';
  constructor(
    public obj: THREE.Object3D,
    public orbitCenter: Vec3 = { x: 0, y: 0, z: 0 }, // Center of the orbit
    public orbitRadius = 20.0, // meters
    public orbitAltitude = 30.0, // meters
    public orbitSpeed = 2.5, // m/s
  ) {
    super();
  }

  private altGain = 0.1; // Altitude control gain
  private distanceGain = 0.25; // Yaw rate control gain
  private yawGain = 2.0; // Yaw control gain

  public computeTwist(dt: number): Twist {
    // console.log({ orbitCenter: this.orbitCenter });
    const currentPosition = this.obj.position.clone();
    const center = new THREE.Vector3(this.orbitCenter.x, this.orbitCenter.y, this.orbitCenter.z);
    center.z = this.orbitCenter.z + this.orbitAltitude;
    const centerPos = this.refToLocal(center);
    // debugger
    const course = normalizeAngleToPi(this.obj.rotation.z + Math.atan2(centerPos.y, centerPos.x) - Math.PI / 2);
    const distanceToCenter = centerPos.length();
    const distanceError = distanceToCenter - this.orbitRadius;
    if (Math.abs(distanceError) < 5) {
      this.status = 'Orbiting';
    } else {
      this.status = 'Correcting to orbit';
    }
    const courseCorrection = constrain(
      this.distanceGain * distanceError,
      -Math.PI / 2,
      Math.PI / 2,
    );
    const correncteFoDistanceError = normalizeAngleToPi(course + courseCorrection);
    const courseError = normalizeAngleToPi(correncteFoDistanceError - this.obj.rotation.z);
    const desired_yaw_rate = constrain(this.yawGain * courseError, -1.0, 1.0);
    const alt_error = this.orbitAltitude + this.orbitCenter.z - currentPosition.z; // positive if below target
    let desired_vz = this.altGain * alt_error;
    return { linear: { x: this.orbitSpeed, y: 0, z: desired_vz }, angular: { x: 0, y: 0, z: desired_yaw_rate } };
  }

  private orbitRoot: THREE.Group | null = null;
  public visualization(on: boolean): void {
    if (this.orbitRoot) {
      this.obj.parent?.remove(this.orbitRoot); // Remove the previous orbit root if it exists
      this.orbitRoot = null;
    }
    if (!on) {
      return; 
    }
    this.orbitRoot = new THREE.Group();
    this.orbitRoot.position.set(this.orbitCenter.x, this.orbitCenter.y, this.orbitCenter.z);
    const curve = new THREE.EllipseCurve(
      0, 0, 
      this.orbitRadius, this.orbitRadius,
      0, 2 * Math.PI, 
      false, 
      0 
    );
    const path = new THREE.Path(curve.getPoints(50));
    const geometry = new THREE.BufferGeometry().setFromPoints(path.getPoints());
    const material = new THREE.LineBasicMaterial({ color: 0x00ff00, linewidth: 1 });
    const orbitCircle = new THREE.Line(geometry, material);
    orbitCircle.position.z = this.orbitAltitude;
    const cylinderGeometry = new THREE.CylinderGeometry(2.5, 2.5, 2, 32);
    const cylinderMaterial = new THREE.MeshBasicMaterial({ 
      color: 0x00ff00, 
      transparent: true, 
      opacity: 0.15, 
      side: THREE.DoubleSide 
    });
    this.orbitRoot.add(orbitCircle);
    const pill = new THREE.Mesh(cylinderGeometry, cylinderMaterial);
    pill.rotation.x = Math.PI / 2; // Rotate to horizontal
    this.orbitRoot.add(pill);
    this.orbitRoot.name = "OrbitRoot";
    this.obj.parent?.add(this.orbitRoot); // Add the orbit root to the parent of
  }

  public getStatus(): string {
    return this.status;
  }

}